import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";
import {
  Fab,
  Button,
  Card,
  Box,
  Collapse,
  IconButton,
} from "@material-ui/core";

import Typography from "../typography";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import TimerIcon from "@material-ui/icons/Timer";
import ListAltIcon from "@material-ui/icons/ListAlt";
import FitnessCenterIcon from "@material-ui/icons/FitnessCenter";
import { gql } from "@apollo/client";
import { styled, useTheme } from "@material-ui/styles";
import { ActionLink } from "../atoms";
import AddIcon from "@material-ui/icons/Add";
import { injectIntl } from "react-intl";
import messages from "./Trainings.intl";
import { IStyledArguments } from "../../types/styled-arguments";
import Backdrop from "@material-ui/core/Backdrop";
import CircularProgress from "@material-ui/core/CircularProgress";

export const GET_TRAININGS = gql`
  query {
    allTrainingSessions {
      id
      name
      trainingDuration
      sections {
        name
        exercises {
          id
          name
          objectives
          shortDescription
        }
        durations {
          exerciseId
          duration
        }
        sectionDuration
      }
    }
    allExercises {
      id
      name
      shortDescription
      category
      subcategory
      objectives
      taskDevelopment
    }
  }
`;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: "#fff",
    },
  })
);

// Main Container
const TrainingsContainer = styled("div")(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(2),
  minHeight: "60vh",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

const FooterContainer = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  justifyContent: "flex-end",
  padding: theme.spacing(2),
  height: 90,
  width: "auto",
  right: 0,
  position: "fixed",
  bottom: 70,
  zIndex: 1200,
}));

// Card de entrenamiento — superficie plana, un único acento
const TrainingCard = styled(Card)(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: "16px",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  marginBottom: theme.spacing(2),
  overflow: "hidden",
  transition: "box-shadow 0.2s ease",
  "&:hover": {
    boxShadow: "0 4px 16px rgba(16, 21, 26, 0.08)",
  },
  [theme.breakpoints.down("sm")]: {
    borderRadius: "14px",
    marginBottom: theme.spacing(1.5),
  },
}));

const TrainingHeader = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(2, 2.5),
  cursor: "pointer",
  transition: "background 0.2s ease",
  "&:hover": {
    background: theme.palette.extra.backgroundGrey,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5, 2),
  },
}));

const TrainingHeaderLeft = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  flex: 1,
}));

const TrainingIcon = styled("div")(({ theme }: IStyledArguments) => ({
  width: 48,
  height: 48,
  borderRadius: "14px",
  background: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    fontSize: "1.5rem",
    color: "#fff",
  },
  [theme.breakpoints.down("sm")]: {
    width: 40,
    height: 40,
    borderRadius: "12px",
    "& svg": {
      fontSize: "1.2rem",
    },
  },
}));

const TrainingName = styled(Typography.Subtitle1)(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  fontSize: "1.1rem",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1rem",
  },
}));

const ExpandButton = styled(IconButton)(({ theme }: IStyledArguments) => ({
  color: theme.palette.primary.main,
  background: theme.palette.extra.accentSoft,
  transition: "background 0.2s ease",
  "&:hover": {
    background: theme.palette.extra.accentSoft,
  },
}));

const TrainingContent = styled("div")(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(0, 2.5, 2.5, 2.5),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0, 2, 2, 2),
  },
}));

// Card de sección (anidada)
const SectionCard = styled(Card)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  borderRadius: "14px",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  marginBottom: theme.spacing(1.5),
  boxShadow: "none",
  overflow: "hidden",
}));

const SectionHeader = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.5, 2),
  cursor: "pointer",
  transition: "background 0.2s ease",
  "&:hover": {
    background: theme.palette.extra.accentSoft,
  },
}));

const SectionHeaderLeft = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

const SectionIcon = styled("div")(({ theme }: IStyledArguments) => ({
  width: 36,
  height: 36,
  borderRadius: "10px",
  background: theme.palette.extra.accentSoft,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    fontSize: "1.2rem",
    color: theme.palette.primary.main,
  },
}));

const SectionName = styled("div")(({ theme }: IStyledArguments) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontSize: "0.95rem",
}));

const DurationBadge = styled("span")(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.accentSoft,
  border: "1px solid rgba(14, 124, 134, 0.3)",
  borderRadius: 16,
  padding: theme.spacing(0.5, 1.5),
  fontSize: "0.75rem",
  fontWeight: 700,
  color: theme.palette.primary.dark,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  "& svg": {
    fontSize: "0.9rem",
  },
}));

const SectionContent = styled("div")(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(0, 2, 2, 2),
}));

// Fila de ejercicio
const ExerciseItem = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.background.paper,
  borderRadius: "12px",
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(1),
  border: `1px solid ${theme.palette.extra.borderColor}`,
  transition: "border-color 0.2s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
  },
  "&:last-child": {
    marginBottom: 0,
  },
}));

const ExerciseItemHeader = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(0.5),
}));

const ExerciseItemIcon = styled("div")(({ theme }: IStyledArguments) => ({
  width: 24,
  height: 24,
  borderRadius: "6px",
  background: theme.palette.extra.accentSoft,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& svg": {
    fontSize: "0.9rem",
    color: theme.palette.primary.main,
  },
}));

const ExerciseItemName = styled("div")(({ theme }: IStyledArguments) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontSize: "0.9rem",
}));

const ExerciseItemDescription = styled(Typography.Subtitle2)(({ theme }: IStyledArguments) => ({
  color: theme.palette.text.secondary,
  fontSize: "0.8rem",
  lineHeight: 1.5,
  marginLeft: theme.spacing(4),
}));

const ActionButtons = styled(Box)(({ theme }: IStyledArguments) => ({
  display: "flex",
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
}));

const DetailsButton = styled(Button)(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  color: "#fff",
  fontWeight: 700,
  borderRadius: "12px",
  padding: theme.spacing(1, 3),
  textTransform: "none",
  transition: "background 0.2s ease",
  "&:hover": {
    background: theme.palette.primary.dark,
  },
}));

// Estado vacío
const EmptyStateCard = styled(Card)(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  borderRadius: "20px",
  border: `2px dashed ${theme.palette.extra.borderColor}`,
  padding: theme.spacing(6),
  textAlign: "center",
  boxShadow: "none",
}));

const EmptyStateIcon = styled("div")(({ theme }: IStyledArguments) => ({
  width: 80,
  height: 80,
  borderRadius: "50%",
  background: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "0 auto",
  marginBottom: theme.spacing(2),
  "& svg": {
    fontSize: "2.5rem",
    color: "#fff",
  },
}));

const EmptyStateTitle = styled(Typography.H5)(({ theme }: IStyledArguments) => ({
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
}));

const FloatingAddButton = styled(Fab)(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  boxShadow: "0 6px 14px rgba(16, 21, 26, 0.25)",
  transition: "background 0.2s ease",
  "&:hover": {
    background: theme.palette.primary.dark,
  },
}));

const TrainingsComponent = injectIntl(({ intl }) => {
  const classes = useStyles();
  const theme = useTheme<Theme>();

  const { data, loading } = useQuery<any>(GET_TRAININGS);
  const [allTrainings, setAllTrainings] = useState<any>();
  const [allExercises, setAllExercises] = useState<any>();
  const [open, setOpen] = React.useState(false);
  const [expandedTrainings, setExpandedTrainings] = useState<string[]>([]);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  useEffect(() => {
    if (loading) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [loading]);

  useEffect(() => {
    if (data && data.allTrainingSessions) {
      setAllTrainings(data.allTrainingSessions);
    }
    if (data && data.allExercises) {
      setAllExercises(data.allExercises);
    }
  }, [data]);

  const toggleTraining = (id: string) => {
    setExpandedTrainings((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleSection = (id: string) => {
    setExpandedSections((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const isTrainingExpanded = (id: string) => expandedTrainings.includes(id);
  const isSectionExpanded = (id: string) => expandedSections.includes(id);

  return (
    <TrainingsContainer>
      <Backdrop className={classes.backdrop} open={open}>
        <CircularProgress color="inherit" />
      </Backdrop>
      
      {!loading && (!allTrainings || allTrainings.length === 0) && (
        <EmptyStateCard elevation={0}>
          <EmptyStateIcon>
            <ListAltIcon />
          </EmptyStateIcon>
          <EmptyStateTitle>
            {allExercises && allExercises.length > 0
              ? intl.formatMessage(messages.noResults)
              : intl.formatMessage(messages.noExercises)}
          </EmptyStateTitle>
        </EmptyStateCard>
      )}
      
      {allTrainings &&
        allTrainings.map((training: any) => (
          <TrainingCard key={`training-${training.id}`} elevation={0}>
            <TrainingHeader onClick={() => toggleTraining(training.id)}>
              <TrainingHeaderLeft>
                <TrainingIcon>
                  <ListAltIcon />
                </TrainingIcon>
                <TrainingName>{training.name}</TrainingName>
              </TrainingHeaderLeft>
              <ExpandButton size="small">
                {isTrainingExpanded(training.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </ExpandButton>
            </TrainingHeader>
            
            <Collapse in={isTrainingExpanded(training.id)}>
              <TrainingContent>
                {training.sections &&
                  training.sections.map((section: any, sectionIndex: number) => {
                    const sectionId = `${training.id}-${sectionIndex}`;
                    return (
                      <SectionCard key={sectionId} elevation={0}>
                        <SectionHeader onClick={() => toggleSection(sectionId)}>
                          <SectionHeaderLeft>
                            <SectionIcon>
                              <FitnessCenterIcon />
                            </SectionIcon>
                            <SectionName>{section.name}</SectionName>
                          </SectionHeaderLeft>
                          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                            <DurationBadge>
                              <TimerIcon />
                              {section.sectionDuration} min
                            </DurationBadge>
                            <IconButton size="small" style={{ color: theme.palette.primary.main }}>
                              {isSectionExpanded(sectionId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                          </Box>
                        </SectionHeader>
                        
                        <Collapse in={isSectionExpanded(sectionId)}>
                          <SectionContent>
                            {section.exercises &&
                              section.exercises.map((exercise: any) => (
                                <ExerciseItem key={`exercise-${exercise.id}`}>
                                  <ExerciseItemHeader>
                                    <ExerciseItemIcon>
                                      <FitnessCenterIcon />
                                    </ExerciseItemIcon>
                                    <ExerciseItemName>{exercise.name}</ExerciseItemName>
                                  </ExerciseItemHeader>
                                  <ExerciseItemDescription>
                                    {exercise.shortDescription}
                                  </ExerciseItemDescription>
                                </ExerciseItem>
                              ))}
                          </SectionContent>
                        </Collapse>
                      </SectionCard>
                    );
                  })}
                
                <ActionButtons>
                  <ActionLink to={`/trainings/${training.id}`}>
                    <DetailsButton variant="contained">
                      {intl.formatMessage(messages.details)}
                    </DetailsButton>
                  </ActionLink>
                </ActionButtons>
              </TrainingContent>
            </Collapse>
          </TrainingCard>
        ))}

      <FooterContainer>
        {allExercises && allExercises.length > 0 && (
          <ActionLink to="/library/newtraining">
            <FloatingAddButton
              aria-label="add training"
              color="secondary"
            >
              <AddIcon />
            </FloatingAddButton>
          </ActionLink>
        )}
      </FooterContainer>
    </TrainingsContainer>
  );
});

export default TrainingsComponent;
